import { RouteProp } from '@react-navigation/native';
import React, { useCallback, useEffect, useMemo } from 'react';
import {
    ClientRecordDetailsTab,
    ProfilesTabParamList,
    useProfilesTabNavigation,
} from '../../../../../utils/appNavigationNames';
import ClientRecordDetails from '../../../../shared/ClientRecord/ClientRecordDetails';
import { useOrganizationClientRecords } from '../../../../../hooks/api/useOrganizationClientRecords';
import { AnimateLoadedContent } from '../../../../v2BaseComponents/Loader';
import { TabOption } from '../../../../v2BaseComponents/TabView';
import { getIntl } from '../../../../../utils/intl';
import { clientRecordDetailsToClientRecord, clientRecordStatusTypeGuard } from '../../../../../utils/clientRecordsUtil';
import { isPresent } from 'ts-is-present';
import ClientRecordInvitationDetails from '../../../../shared/ClientRecord/ClientRecordInvitationDetails';
import { useIsOrganizationBillingAdmin, useOrganizationId } from '../../../../../hooks/api/useOrganization';
import { NoteQueryArgs } from '../../../../../hooks/api/useNotes';
import usePastClientRecordNotes from '../../../../../hooks/api/usePastClientRecordNotes';
import NotesList from '../../../../shared/Notes/NotesList';
import { ActionButtonLayout } from '../../../../v2BaseComponents/ActionButtonLayout';
import MainContentContainer from '../../../../v2BaseComponents/MainContentContainer';
import ClientRecordHistoryContent from './ClientRecordHistoryContent';
import OrganizationClientRecordFiles from './OrganizationClientRecordFiles/index';
import ClientRecordActivity from './ClientRecordActivity';
import { View } from 'react-native';
import { LayoutDependentTabView } from '../../../../v2BaseComponents/TabView';
import useFeatureToggles from '../../../../../hooks/api/useFeatureToggles';
import useOrganizationPaymentIntegration from '../../../../../hooks/api/useOrganizationPaymentIntegration';
import ClientRecordInvoices from './ClientRecordInvoices';
import ClientRecordBillingContact from './ClientRecordBillingContact';

const intl = getIntl('clientRecordDetailsScreen', {
    details: 'Details',
    invitationDetails: 'Invitation Details',
    notes: 'Internal Notes',
    history: 'History',
    files: 'Organization Files',
    activity: 'Activity',
    clientRecordHistory: 'Client Record History',
    invoices: 'Invoices',
    billingContact: 'Billing Contact',
});

export type ClientRecordScreenOwnProps = {};

export type ClientRecordScreenProps = ClientRecordScreenOwnProps & {
    route: RouteProp<ProfilesTabParamList, 'client-record'>;
};

const ClientRecordScreen: React.FC<ClientRecordScreenProps> = ({ route }) => {
    const hasInvoicingFeature = useFeatureToggles().featureToggles.organizationClientInvoicing;
    const isClientBillingEnabled = useOrganizationPaymentIntegration({}).isOrganizationInValidStripeState;
    const isBillingAdmin = useIsOrganizationBillingAdmin();
    const isInvoicingEnabledForClient = isClientBillingEnabled && !!hasInvoicingFeature && isBillingAdmin;
    const navigator = useProfilesTabNavigation();
    const organizationId = useOrganizationId();
    const {
        isLoadingClientRecord,
        clientRecord: clientRecordDetails,
        clientRecords,
    } = useOrganizationClientRecords({
        organizationClientRecordId: route.params.clientRecordId,
    });
    const clientRecord = clientRecordDetails ? clientRecordDetailsToClientRecord(clientRecordDetails) : null;
    const noteQueryArgs = useMemo<NoteQueryArgs>(
        () => ({
            entityId: route.params.clientRecordId,
            organizationClientRecordId: route.params.clientRecordId,
            organizationId,
            entityType: 'organization-client-record',
        }),
        [route.params.clientRecordId, organizationId],
    );
    // When a client is reinvited, a new client record is created while notes
    // remain attached to prior (inactive) records. Surface those notes in the
    // notes tab by fetching them for each past record and merging them in.
    const pastOrganizationClientRecordIds = useMemo(() => {
        const match = clientRecords.find((record) => record.organizationClientRecordId === route.params.clientRecordId);
        return match?.pastOrganizationClientRecords?.map((_) => _.organizationClientRecordId) ?? [];
    }, [clientRecords, route.params.clientRecordId]);
    const { notes: pastClientRecordNotes } = usePastClientRecordNotes({
        organizationId,
        pastOrganizationClientRecordIds,
    });

    const renderInContentContainer = (content: React.ReactNode) => (
        <ActionButtonLayout scrollable whiteBackground>
            <MainContentContainer flex={1}>{content}</MainContentContainer>
        </ActionButtonLayout>
    );

    const tabOptions: TabOption[] = useMemo<TabOption[]>(
        () =>
            clientRecord
                ? [
                      {
                          value: 'details',
                          title: intl('details'),
                          sideTabContentWidthVariant: 'sm' as const,
                          renderTabContent: () =>
                              renderInContentContainer(
                                  <View style={{ height: '100%', width: '100%' }}>
                                      <ClientRecordDetails organizationClientRecordId={route.params.clientRecordId} />
                                  </View>,
                              ),
                      },
                      !clientRecordStatusTypeGuard.notInvited(clientRecord) &&
                      !clientRecordStatusTypeGuard.archived(clientRecord)
                          ? {
                                value: 'invitation-details',
                                title: intl('invitationDetails'),
                                sideTabContentWidthVariant: 'sm' as const,
                                sideTabTitle: intl('invitationDetails'),
                                renderTabContent: () =>
                                    renderInContentContainer(
                                        <ClientRecordInvitationDetails clientRecordId={route.params.clientRecordId} />,
                                    ),
                            }
                          : null,
                      {
                          value: 'notes',
                          title: intl('notes'),
                          sideTabContentWidthVariant: 'sm' as const,
                          renderTabContent: () => (
                              <NotesList
                                  noteQueryArgs={noteQueryArgs}
                                  additionalNotes={pastClientRecordNotes}
                                  onAddNote={() => {
                                      navigator.navigate('add-client-record-note', {
                                          clientRecordId: route.params.clientRecordId,
                                      });
                                  }}
                                  onViewDetails={(note) => {
                                      navigator.navigate('client-record-note', {
                                          noteId: note.noteId,
                                          clientRecordId: route.params.clientRecordId,
                                      });
                                  }}
                                  emptyStateVariant="organization-client-notes"
                              />
                          ),
                      },
                      clientRecord?.status === 'active'
                          ? {
                                value: 'organization-files',
                                title: intl('files'),
                                sideTabContentWidthVariant: 'md' as const,
                                renderTabContent: () => (
                                    <OrganizationClientRecordFiles activeClientRecord={clientRecord} />
                                ),
                            }
                          : null,
                      clientRecord?.status === 'active'
                          ? {
                                value: 'activity',
                                title: intl('activity'),
                                sideTabContentWidthVariant: 'md' as const,
                                renderTabContent: () => (
                                    <View style={{ height: '100%', width: '100%' }} testID="log-list-tab-container">
                                        <ClientRecordActivity activeClientRecord={clientRecord} />
                                    </View>
                                ),
                            }
                          : null,
                      clientRecord.clientMedicalProfileId
                          ? {
                                value: 'history',
                                title: intl('history'),
                                sideTabContentWidthVariant: 'lg' as const,
                                sideTabTitle: intl('clientRecordHistory'),
                                renderTabContent: () => (
                                    <ClientRecordHistoryContent
                                        clientMedicalProfileId={clientRecord.clientMedicalProfileId!}
                                    />
                                ),
                            }
                          : null,
                      isInvoicingEnabledForClient
                          ? {
                                value: 'invoices',
                                title: intl('invoices'),
                                sideTabContentWidthVariant: 'lg' as const,
                                sideTabTitle: intl('invoices'),
                                renderTabContent: () => (
                                    <ClientRecordInvoices clientRecordId={route.params.clientRecordId} />
                                ),
                            }
                          : null,
                      isInvoicingEnabledForClient
                          ? {
                                value: 'billing-contact',
                                title: intl('billingContact'),
                                sideTabContentWidthVariant: 'md' as const,
                                sideTabTitle: intl('billingContact'),
                                renderTabContent: () => (
                                    <ClientRecordBillingContact clientRecordId={route.params.clientRecordId} />
                                ),
                            }
                          : null,
                  ].filter(isPresent)
                : [],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [clientRecord, noteQueryArgs, pastClientRecordNotes, route.params.clientRecordId, isInvoicingEnabledForClient],
    );
    const getTabOption = useCallback(
        (tab: ClientRecordDetailsTab): TabOption | undefined => tabOptions.find((tabOption) => tabOption.value === tab),
        [tabOptions],
    );

    // tab options are dynamic based on client record status, ensure the selected tab is always valid
    const [selectedTab, setSelectedTab] = React.useState<ClientRecordDetailsTab | null>(
        route.params.defaultTab || null,
    );
    useEffect(() => {
        if (tabOptions.length === 0) {
            setSelectedTab(null);
        } else if (selectedTab === null && tabOptions.length > 0) {
            const maybeAllowedDefaultTab = route.params.defaultTab ? getTabOption(route.params.defaultTab) : null;
            setSelectedTab((maybeAllowedDefaultTab?.value || tabOptions[0].value) as ClientRecordDetailsTab);
        } else {
            const selectedTabOption = selectedTab ? getTabOption(selectedTab) : null;
            if (!selectedTabOption) {
                setSelectedTab(tabOptions[0].value as ClientRecordDetailsTab);
            }
        }
    }, [tabOptions, selectedTab, route.params.defaultTab, getTabOption]);

    // watch for default tabs to change.  This screen may already be mounted when the default tab is changed on a redirect
    const hasTabs = tabOptions.length > 0;
    useEffect(() => {
        if (hasTabs && route.params.defaultTab && getTabOption(route.params.defaultTab)) {
            setSelectedTab(route.params.defaultTab);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasTabs, route.params.defaultTab]);

    const selectedTabOption = selectedTab ? getTabOption(selectedTab) : null;

    return (
        <AnimateLoadedContent isLoading={isLoadingClientRecord || !selectedTabOption}>
            {() => (
                <LayoutDependentTabView
                    restrictedWidthVariant="lg"
                    tabs={tabOptions}
                    selectedTab={selectedTab!}
                    onChange={(tab) => setSelectedTab(tab as ClientRecordDetailsTab)}
                />
            )}
        </AnimateLoadedContent>
    );
};

export default React.memo(ClientRecordScreen);
